import { App } from 'antd';
import { useMsal } from '@azure/msal-react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarSlot, PreSalesBookingChannel, Result, Slot } from '../../../dto/model';
import { strings } from '../../../lang';
import { heatBookingPageService } from '../../../services/heat-booking-page-service';
import { ZeegBookingCalendarInfo } from './pre-sales-questionnaire-utils';
import { PreSalesQuestionnaireCustomer } from './pre-sales-questionnaire-types';

type UsePreSalesVideoSalesBookingParams = {
  crmId?: string;
  signupId?: string;
  customer?: PreSalesQuestionnaireCustomer;
  zeegBookingLinkInfo: ZeegBookingCalendarInfo | null;
  /** The unified-flow lane being booked; the backend derives the TMVT booking channel from it. */
  bookingLane?: string;
  /** All lanes that were eligible, in priority order; the backend uses the first as Priority-1. */
  eligibleBookingLanes?: string[];
  /** Why the agent booked outside the first offered lane; absent when the first lane was booked. */
  bookingLaneReason?: string;
};

export const usePreSalesVideoSalesBooking = ({
  crmId,
  signupId,
  customer,
  zeegBookingLinkInfo,
  bookingLane,
  eligibleBookingLanes,
  bookingLaneReason,
}: UsePreSalesVideoSalesBookingParams) => {
  const { notification } = App.useApp();
  const { accounts } = useMsal();
  const [bookedSlot, setBookedSlot] = useState<Slot | null>(null);
  // Keeps the channel of the link the Zeeg booking was made through, so a retried recording sends it unchanged.
  const pendingBookingRef = useRef<{
    key: string;
    channel: PreSalesBookingChannel;
    result: Result<Slot>;
  } | null>(null);
  const bookingInProgressRef = useRef(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const reset = useCallback(() => {
    pendingBookingRef.current = null;
    bookingInProgressRef.current = false;
    setBookedSlot(null);
  }, []);

  useEffect(() => {
    reset();
  }, [crmId, signupId, reset]);

  const bookSlot = useCallback(async (
    slot: CalendarSlot,
    accessToken: string,
    reason?: string,
  ) => {
    const customerName = `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`.trim();
    if (!customerName || !customer?.email) {
      notification.error({ message: strings.preSalesQuestionnaire.videoBookingMissingContact });
      return {
        isSuccess: false,
        errors: [strings.preSalesQuestionnaire.videoBookingMissingContact],
        data: {} as Slot,
      };
    }

    // The legacy flow identifies the lead by CRM deal id; the unified flow has no deal id and uses the
    // signup id instead. Either one is enough to book and record the channel.
    const hasDealId = Boolean(crmId && Number.isSafeInteger(Number(crmId)));
    const identifier = hasDealId ? { crmId: crmId as string } : signupId ? { signupId } : null;
    if (!identifier) {
      notification.error({ message: strings.preSalesQuestionnaire.videoBookingMissingDeal });
      return {
        isSuccess: false,
        errors: [strings.preSalesQuestionnaire.videoBookingMissingDeal],
        data: {} as Slot,
      };
    }

    if (!zeegBookingLinkInfo?.ownerSlug || !zeegBookingLinkInfo.eventTypeSlug) {
      return {
        isSuccess: false,
        errors: [strings.preSalesQuestionnaire.videoBookingConfigurationError],
        data: {} as Slot,
      };
    }

    const bookingKey = identifier.crmId ?? identifier.signupId ?? '';
    bookingInProgressRef.current = true;
    try {
      const customQueryParams = Object.fromEntries(
        Object.entries({
          deal_id: identifier.crmId,
          lead_id: identifier.signupId,
          signup_id: identifier.signupId,
          first_name: customer?.firstName,
          last_name: customer?.lastName,
          email: customer?.email,
          user: accounts[0]?.username,
          source: 'tmvt',
          stage: 'presales',
          environemnt: process.env.REACT_APP_ENV,
          bookingLink: zeegBookingLinkInfo.link,
        }).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0),
      );

      const pendingBooking = pendingBookingRef.current?.key === bookingKey
        ? pendingBookingRef.current
        : null;
      let bookingResponse = pendingBooking?.result ?? null;
      // Links without a channel predate PreSales channel routing, which recorded every Zeeg booking as VideoSales.
      const channel = pendingBooking?.channel ?? zeegBookingLinkInfo.channel ?? 'VideoSales';

      if (!bookingResponse) {
        bookingResponse = await heatBookingPageService.bookPreSalesZeegSlot(
          {
            slotDate: slot.startDate.format('YYYY-MM-DDTHH:mm:ss'),
            name: customerName,
            email: customer.email,
            ownerSlug: zeegBookingLinkInfo.ownerSlug,
            eventTypeSlug: zeegBookingLinkInfo.eventTypeSlug,
            // Unified flow only: the lane and (for a non-Priority-1 lane) the reason, so the booking
            // analytics event carries them. Both are absent for the legacy deal-id flow.
            ...(bookingLane && { bookingLane }),
            ...(eligibleBookingLanes?.length && { eligibleLanes: eligibleBookingLanes }),
            ...((reason ?? bookingLaneReason) && { bookingLaneReason: reason ?? bookingLaneReason }),
            ...(Object.keys(customQueryParams).length > 0 && { customQueryParams }),
          },
          accessToken,
        );

        if (!bookingResponse.isSuccess) {
          return bookingResponse;
        }

        pendingBookingRef.current = { key: bookingKey, channel, result: bookingResponse };
      }

      const confirmation = await heatBookingPageService.recordPreSalesBookingChannel(
        identifier,
        channel,
        accessToken,
        bookingLane,
      );
      if (!confirmation.success) {
        return {
          isSuccess: false,
          errors: [confirmation.error ?? confirmation.message ?? strings.preSalesQuestionnaire.videoBookingConfirmationError],
          data: bookingResponse.data,
        };
      }

      pendingBookingRef.current = null;
      return bookingResponse;
    } finally {
      bookingInProgressRef.current = false;
    }
  }, [accounts, bookingLane, bookingLaneReason, crmId, customer, eligibleBookingLanes, notification, signupId, zeegBookingLinkInfo]);

  const loadSlots = useCallback(async (calendarWeek: string, accessToken: string) => {
    if (!zeegBookingLinkInfo?.ownerSlug || !zeegBookingLinkInfo.eventTypeSlug) {
      throw new Error(strings.preSalesQuestionnaire.videoBookingConfigurationError);
    }

    const response = await heatBookingPageService.getPreSalesZeegSlots(
      calendarWeek,
      accessToken,
      zeegBookingLinkInfo.ownerSlug,
      zeegBookingLinkInfo.eventTypeSlug,
    );

    if (!response.success) {
      throw new Error(response.error ?? response.message ?? strings.preSalesQuestionnaire.videoSlotsError);
    }

    return { slots: response.data ?? [] };
  }, [zeegBookingLinkInfo]);

  const handleBooking = useCallback((isSuccess: boolean, nextBookedSlot?: Slot, error?: string) => {
    if (!isMountedRef.current) {
      return;
    }

    if (isSuccess && nextBookedSlot) {
      setBookedSlot({
        ...nextBookedSlot,
        startDate: dayjs(nextBookedSlot.startDate),
        endDate: dayjs(nextBookedSlot.endDate),
      });
      notification.success({ message: strings.preSalesQuestionnaire.videoBookingSuccess });
      return;
    }

    if (error) {
      notification.error({
        message: strings.preSalesQuestionnaire.videoBookingError,
        description: error,
      });
    }
  }, [notification]);

  return {
    bookedSlot,
    loadSlots,
    bookSlot,
    handleBooking,
    clearResult: () => setBookedSlot(null),
    hasPendingBooking: () =>
      bookingInProgressRef.current || pendingBookingRef.current !== null,
  };
};
